import type {Action} from '@github-ui/action-bar'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  getTokensByType,
  replaceAllFiltersByTypeInSearchQuery,
  replaceTokensDifferentially,
} from '@github-ui/filter-query'
import {AssigneeRepositoryPicker} from '@github-ui/item-picker-assignee-relay'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import {formatActorLogin} from '@github-ui/item-picker-assignee-tsq/utils/assignee-picker'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import type {ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import {IssueTypePicker} from '@github-ui/item-picker-issue-type-tsq'
import {LabelPicker, type LabelPickerLabel} from '@github-ui/item-picker-label-tsq'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import {MilestonePicker} from '@github-ui/item-picker-milestone-tsq'
import type {ProjectPickerBaseProject as ProjectPickerProject} from '@github-ui/item-picker-project'
import {ProjectPicker} from '@github-ui/item-picker-project-tsq'
import type {ScopedRepository} from '@github-ui/list-view-items-issues-prs/Query'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {
  IssueOpenedIcon,
  MentionIcon,
  MilestoneIcon,
  PersonIcon,
  ProjectIcon,
  TagIcon,
  TriangleDownIcon,
} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import type React from 'react'
import {type Dispatch, type SetStateAction, useCallback, useMemo, useState} from 'react'

import {LABELS} from './labels'
import styles from './repository-filter-actions.module.css'

export type RepositoryFilterPickerProps = {
  nested?: boolean
  repo: ScopedRepository
  applySectionFilter: (query: string) => void
  query: string
}

type RepositoryFilterActionsProps = {
  repo: ScopedRepository | undefined
  applySectionFilter: RepositoryFilterPickerProps['applySectionFilter']
  issueTypesEnabled: boolean
  query: string
}

export function useRepositoryFilterActions({
  repo,
  applySectionFilter,
  issueTypesEnabled,
  query,
}: RepositoryFilterActionsProps): Action[] {
  return useMemo(() => {
    if (!repo) return []

    const pickerProps = {repo, applySectionFilter, query}
    const actions: Action[] = [
      {key: 'authors', render: nested => <RepositoryAuthorFilter {...pickerProps} nested={nested} />},
      {key: 'labels', render: nested => <RepositoryLabelFilter {...pickerProps} nested={nested} />},
      {key: 'projects', render: nested => <RepositoryProjectFilter {...pickerProps} nested={nested} />},
      {key: 'milestones', render: nested => <RepositoryMilestoneFilter {...pickerProps} nested={nested} />},
      {key: 'assignees', render: nested => <RepositoryAssigneeFilter {...pickerProps} nested={nested} />},
    ]

    if (issueTypesEnabled) {
      actions.push({
        key: 'issue-types',
        render: nested => <RepositoryIssueTypeFilter {...pickerProps} nested={nested} />,
      })
    }

    return actions
  }, [applySectionFilter, issueTypesEnabled, query, repo])
}

export function RepositoryAuthorFilter({
  repo: {name: repo, owner},
  applySectionFilter,
  nested,
  query,
}: RepositoryFilterPickerProps) {
  const currentAuthorTokens = useMemo(() => getTokensByType(query, 'author'), [query])
  const includeBots = isFeatureEnabled('issues_react_include_bots_in_pickers')
  const onSelectionChanged = useCallback(
    (selectedAuthors: AssigneePickerAssignee[]) => {
      const authors = selectedAuthors.map(formatActorLogin)
      applySectionFilter(replaceAllFiltersByTypeInSearchQuery(query, authors, 'author'))
    },
    [applySectionFilter, query],
  )

  return (
    <AssigneeRepositoryPicker
      name="author"
      selectionVariant="single"
      readonly={false}
      title={LABELS.filters.authorLabel}
      assigneeTokens={currentAuthorTokens}
      assignees={[]}
      repo={repo}
      owner={owner}
      onSelectionChange={onSelectionChanged}
      anchorElement={nested ? NestedAuthorsAnchor : AuthorsAnchor}
      showNoMatchItem
      includeAuthorableBots={includeBots}
      includeAssignableBots={false}
    />
  )
}

export function RepositoryLabelFilter({nested, repo, applySectionFilter, query}: RepositoryFilterPickerProps) {
  const noLabelItem = useMemo<ExtendedItemProps<LabelPickerLabel>>(
    () => ({
      id: SPECIAL_VALUES.noLabelsData.id,
      description: '',
      descriptionVariant: 'inline',
      children: (
        <VerifiedHTMLText
          /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe */
          html={SPECIAL_VALUES.noLabelsData.name as SafeHTMLString}
          title={SPECIAL_VALUES.noLabelsData.description}
        />
      ),
      source: SPECIAL_VALUES.noLabelsData as LabelPickerLabel,
      selected: query.includes('no:label'),
      groupId: '',
      leadingVisual: () => <div className={styles.LabelColorIndicator} />,
    }),
    [query],
  )
  const currentLabelTokens = useMemo(() => {
    const tokens = getTokensByType(query, 'label')
    return tokens.length > 0 ? tokens : undefined
  }, [query])
  const onSelectionChanged = useCallback(
    (selectedLabels: LabelPickerLabel[]) => {
      const labels = selectedLabels.map(label =>
        label.id === SPECIAL_VALUES.noLabelsData.id ? 'no:label' : label.name,
      )
      applySectionFilter(replaceTokensDifferentially(query, labels, 'label'))
    },
    [applySectionFilter, query],
  )

  return (
    <LabelPicker
      anchorElement={nested ? NestedLabelsAnchor : LabelsAnchor}
      title={LABELS.filters.labelsLabel}
      showEditLabelsButton={false}
      repo={repo.name}
      owner={repo.owner}
      readonly={false}
      onSelectionChange={onSelectionChanged}
      noLabelOption={noLabelItem}
      labels={[]}
      labelNames={currentLabelTokens}
      canCreateLabel={false}
      enableSuggestedLabels
      includeArchived
    />
  )
}

export function RepositoryProjectFilter({repo, applySectionFilter, nested, query}: RepositoryFilterPickerProps) {
  const currentProjectsTokens = useMemo(() => getTokensByType(query, 'project'), [query])
  const onSelectionChanged = useCallback(
    (selectedProjects: ProjectPickerProject[]) => {
      const projects = selectedProjects.map(project => `${repo.owner}/${project.number}`)
      const newQuery = replaceTokensDifferentially(query, projects, 'project')
      if (newQuery !== query) applySectionFilter(newQuery)
    },
    [applySectionFilter, query, repo.owner],
  )
  const currentSelectedProjects = useCallback(
    (projects: ProjectPickerProject[]) => {
      const selectedProjects: ProjectPickerProject[] = []
      for (const token of currentProjectsTokens) {
        const foundProject = projects.find(project => {
          const [repoName, number] = token.split('/')
          return project.url.endsWith(`/${repoName}/projects/${number}`)
        })
        if (foundProject?.id && !selectedProjects.includes(foundProject)) selectedProjects.push(foundProject)
      }
      return selectedProjects
    },
    [currentProjectsTokens],
  )

  return (
    <ProjectPicker
      pickerId="list-project-filter"
      anchorElement={nested ? NestedProjectsAnchor : ProjectsAnchor}
      repo={repo.name}
      owner={repo.owner}
      onSave={onSelectionChanged}
      selectedProjects={[]}
      readonly={false}
      getSelectedProjects={currentSelectedProjects}
      title={LABELS.filters.projectsLabel}
    />
  )
}

export function RepositoryMilestoneFilter({nested, repo, applySectionFilter, query}: RepositoryFilterPickerProps) {
  const noMilestoneItem = useMemo<ExtendedItemProps<MilestonePickerMilestone>>(
    () => ({
      id: SPECIAL_VALUES.noMilestoneData.id,
      description: '',
      descriptionVariant: 'inline',
      /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe */
      children: <VerifiedHTMLText html={SPECIAL_VALUES.noMilestoneData.title as SafeHTMLString} />,
      source: SPECIAL_VALUES.noMilestoneData as MilestonePickerMilestone,
      groupId: '',
      selected: query.includes('no:milestone'),
      leadingVisual: () => <MilestoneIcon />,
    }),
    [query],
  )
  const currentMilestoneTokens = useMemo(() => getTokensByType(query, 'milestone'), [query])
  const [activeMilestone, setActiveMilestone] = useActiveMilestone(currentMilestoneTokens)
  const onSelectionChanged = useCallback(
    (selectedMilestones: MilestonePickerMilestone[]) => {
      const isNegatedFilter = selectedMilestones[0]?.id === SPECIAL_VALUES.noMilestoneData.id
      if (selectedMilestones[0]) setActiveMilestone(selectedMilestones[0])
      applySectionFilter(
        replaceAllFiltersByTypeInSearchQuery(
          query,
          selectedMilestones.map(milestone => milestone.title),
          'milestone',
          isNegatedFilter,
        ),
      )
    },
    [applySectionFilter, query, setActiveMilestone],
  )

  return (
    <MilestonePicker
      repo={repo.name}
      activeMilestone={activeMilestone}
      anchorElement={nested ? NestedMilestonesAnchor : MilestonesAnchor}
      owner={repo.owner}
      onSelectionChange={onSelectionChanged}
      noMilestoneItem={noMilestoneItem}
      title={LABELS.filters.milestonesLabel}
      canCreateMilestone={false}
    />
  )
}

export function RepositoryAssigneeFilter({
  repo: {name: repo, owner},
  applySectionFilter,
  nested,
  query,
}: RepositoryFilterPickerProps) {
  const currentAssigneeTokens = useMemo(() => getTokensByType(query, 'assignee').slice(-1), [query])
  const noAssigneeOption = useMemo<ExtendedItemProps<AssigneePickerAssignee>>(
    () => ({
      id: SPECIAL_VALUES.noAssigneeData.id,
      description: '',
      descriptionVariant: 'inline',
      /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe */
      children: <VerifiedHTMLText html={SPECIAL_VALUES.noAssigneeData.login as SafeHTMLString} />,
      source: SPECIAL_VALUES.noAssigneeData as AssigneePickerAssignee,
      selected: query.includes('no:assignee'),
      leadingVisual: () => <PersonIcon />,
    }),
    [query],
  )
  const onSelectionChanged = useCallback(
    (selectedAssignees: AssigneePickerAssignee[]) => {
      const assignees = selectedAssignees.map(assignee =>
        assignee.id === SPECIAL_VALUES.noAssigneeData.id ? 'no:assignee' : formatActorLogin(assignee),
      )
      applySectionFilter(replaceTokensDifferentially(query, assignees, 'assignee'))
    },
    [applySectionFilter, query],
  )

  return (
    <AssigneeRepositoryPicker
      name="assignee"
      selectionVariant="single"
      readonly={false}
      title={LABELS.filters.assigneesLabel}
      assigneeTokens={currentAssigneeTokens}
      assignees={[]}
      repo={repo}
      owner={owner}
      onSelectionChange={onSelectionChanged}
      noAssigneeOption={noAssigneeOption}
      anchorElement={nested ? NestedAssigneesAnchor : AssigneesAnchor}
      showNoMatchItem
      includeAuthorableBots={false}
      includeAssignableBots
    />
  )
}

export function RepositoryIssueTypeFilter({
  repo: {owner, name: repo},
  applySectionFilter,
  nested,
  query,
}: RepositoryFilterPickerProps) {
  const noIssueTypeItem = useMemo<ExtendedItemProps<IssueTypePickerIssueType>>(
    () => ({
      id: SPECIAL_VALUES.noTypeData.id,
      description: '',
      descriptionVariant: 'inline',
      /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe */
      children: <VerifiedHTMLText html={SPECIAL_VALUES.noTypeData.name as SafeHTMLString} />,
      source: SPECIAL_VALUES.noTypeData as IssueTypePickerIssueType,
      groupId: '',
      selected: query.includes('no:type'),
    }),
    [query],
  )
  const currentTypeToken = useMemo(() => getTokensByType(query, 'type').at(-1), [query])
  const onSelectionChanged = useCallback(
    (issueTypes: IssueTypePickerIssueType[] | null) => {
      const tokens =
        issueTypes?.map(issueType => (issueType.id === SPECIAL_VALUES.noTypeData.id ? 'no:type' : issueType.name)) ?? []
      applySectionFilter(replaceTokensDifferentially(query, tokens, 'type'))
    },
    [applySectionFilter, query],
  )

  return (
    <IssueTypePicker
      title={LABELS.filters.issueTypeLabel}
      owner={owner}
      repo={repo}
      readonly={false}
      activeIssueType={null}
      issueTypeToken={currentTypeToken}
      onSelectionChange={onSelectionChanged}
      anchorElement={nested ? NestedIssueTypeAnchor : IssueTypeAnchor}
      width="medium"
      noTypeItem={noIssueTypeItem}
    />
  )
}

function useActiveMilestone(
  currentMilestoneTokens: readonly unknown[],
): [MilestonePickerMilestone | null, Dispatch<SetStateAction<MilestonePickerMilestone | null>>] {
  const [activeMilestone, setActiveMilestone] = useState<MilestonePickerMilestone | null>(null)
  const [previousTokens, setPreviousTokens] = useState(currentMilestoneTokens)
  if (currentMilestoneTokens !== previousTokens) {
    setPreviousTokens(currentMilestoneTokens)
    if (currentMilestoneTokens.length === 0 && activeMilestone !== null) {
      setActiveMilestone(null)
      return [null, setActiveMilestone]
    }
  }
  return [activeMilestone, setActiveMilestone]
}

function FilterButton({
  testId,
  label,
  ariaLabel,
  ...props
}: React.HTMLAttributes<HTMLButtonElement> & {testId: string; label: string; ariaLabel?: string}) {
  return (
    <Button
      variant="invisible"
      data-testid={testId}
      trailingVisual={TriangleDownIcon}
      aria-label={ariaLabel ?? `Filter by ${label.toLowerCase()}`}
      className={styles.FilterButton}
      {...props}
    >
      {label}
    </Button>
  )
}

function NestedFilterAnchor({
  label,
  ariaLabel,
  icon: Icon,
  ...props
}: React.HTMLAttributes<HTMLElement> & {label: string; ariaLabel?: string; icon: React.ComponentType}) {
  return (
    <ActionList.Item {...props} aria-label={ariaLabel ?? `Filter by ${label.toLowerCase()}`} role="menuitem">
      <ActionList.LeadingVisual>
        <Icon />
      </ActionList.LeadingVisual>
      {label}...
    </ActionList.Item>
  )
}

const AuthorsAnchor = (props: React.HTMLAttributes<HTMLButtonElement>) => (
  <FilterButton {...props} testId="authors-anchor-button" label={LABELS.filters.author} />
)
const LabelsAnchor = (props: React.HTMLAttributes<HTMLButtonElement>) => (
  <FilterButton {...props} testId="labels-anchor-button" label={LABELS.filters.labels} />
)
const ProjectsAnchor = (props: React.HTMLAttributes<HTMLButtonElement>) => (
  <FilterButton {...props} testId="projects-anchor-button" label={LABELS.filters.projects} />
)
const MilestonesAnchor = (props: React.HTMLAttributes<HTMLButtonElement>) => (
  <FilterButton {...props} testId="milestones-anchor-button" label={LABELS.filters.milestones} />
)
const AssigneesAnchor = (props: React.HTMLAttributes<HTMLButtonElement>) => (
  <FilterButton {...props} testId="assignees-anchor-button" label={LABELS.filters.assignees} />
)
const IssueTypeAnchor = (props: React.HTMLAttributes<HTMLButtonElement>) => (
  <FilterButton
    {...props}
    testId="issue-types-anchor-button"
    label={LABELS.filters.issueType}
    ariaLabel={LABELS.filters.issueTypeLabel}
  />
)
const NestedAuthorsAnchor = (props: React.HTMLAttributes<HTMLElement>) => (
  <NestedFilterAnchor {...props} label={LABELS.filters.author} icon={PersonIcon} />
)
const NestedLabelsAnchor = (props: React.HTMLAttributes<HTMLElement>) => (
  <NestedFilterAnchor {...props} label={LABELS.filters.labels} icon={TagIcon} />
)
const NestedProjectsAnchor = (props: React.HTMLAttributes<HTMLElement>) => (
  <NestedFilterAnchor {...props} label={LABELS.filters.projects} icon={ProjectIcon} />
)
const NestedMilestonesAnchor = (props: React.HTMLAttributes<HTMLElement>) => (
  <NestedFilterAnchor {...props} label={LABELS.filters.milestones} icon={MilestoneIcon} />
)
const NestedAssigneesAnchor = (props: React.HTMLAttributes<HTMLElement>) => (
  <NestedFilterAnchor {...props} label={LABELS.filters.assignees} icon={MentionIcon} />
)
const NestedIssueTypeAnchor = (props: React.HTMLAttributes<HTMLElement>) => (
  <NestedFilterAnchor
    {...props}
    label={LABELS.filters.issueType}
    ariaLabel={LABELS.filters.issueTypeLabel}
    icon={IssueOpenedIcon}
  />
)
